import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Card, Spin, message, Button, Input, Select, Empty, Divider, Checkbox, InputNumber, Alert } from 'antd';
import { SearchOutlined, UserOutlined, CheckOutlined, ClockCircleOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { FaUserMd, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { FaRegCalendarDays } from 'react-icons/fa6';
import drEasyApi from '../../../Api/Customapi';
import { getImg } from '../../../Api/getImg';
import dayjs from 'dayjs';

const SpecialistAssignmentModal = ({
  visible,
  onCancel,
  onAssign,
  booking,
  isMobile,
  handleCompleteClick
}) => {
  const [specialists, setSpecialists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialist, setSelectedSpecialist] = useState(null);
  const [assigningId, setAssigningId] = useState(null);
  const [filterWorkingDay, setFilterWorkingDay] = useState(null);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState([]);
  const [selectedShiftDayIds, setSelectedShiftDayIds] = useState([]);
  const [selectedAvailabilityHour, setSelectedAvailabilityHour] = useState(null);
  const [numberOfSessions, setNumberOfSessions] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [dateError, setDateError] = useState('');
  const [validationMessage, setValidationMessage] = useState(null);
  const [validating, setValidating] = useState(false);
  const [autoValidated, setAutoValidated] = useState(false);

  const workingDays = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
  ];

  const fetchSpecialists = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        PageNumber: pageNumber,
        PageSize: pageSize,
      };

      if (searchTerm.trim()) {
        params.NameSearch = searchTerm;
      }

      if (filterWorkingDay !== null && filterWorkingDay !== undefined) {
        params.WorkingDay = filterWorkingDay;
      }

      const response = await drEasyApi.get('/api/Centers/getMySpecialists', {
        params,
      });

      setSpecialists(response.data?.specialists || []);
      setTotalCount(response.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching specialists:', error);
      message.error('Failed to fetch specialists');
      setSpecialists([]);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, searchTerm, filterWorkingDay]);

  useEffect(() => {
    if (visible) {
      setPageNumber(1);
      setSearchTerm('');
      setFilterWorkingDay(null);
      resetSelection();
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      fetchSpecialists();
    }
  }, [visible, fetchSpecialists]);

  const resetSelection = () => {
    setSelectedSpecialist(null);
    setSelectedClinic(null);
    setSelectedShift(null);
    setSelectedDaysOfWeek([]);
    setSelectedShiftDayIds([]);
    setSelectedAvailabilityHour(null);
    setNumberOfSessions(1);
    setSelectedDate(null);
    setDateError('');
    setValidationMessage(null);
    setAutoValidated(false);
  };

  const handleSearch = useCallback((value) => {
    setSearchTerm(value);
    setPageNumber(1);
  }, []);

  const handleWorkingDayChange = useCallback((value) => {
    setFilterWorkingDay(value);
    setPageNumber(1);
  }, []);

  const handleSpecialistSelect = (specialist) => {
    if (selectedSpecialist?.specialistId === specialist.specialistId) {
      resetSelection();
    } else {
      setSelectedSpecialist(specialist);
      setSelectedClinic(null);
      setSelectedShift(null);
      setSelectedDaysOfWeek([]);
      setSelectedShiftDayIds([]);
      setSelectedAvailabilityHour(null);
      setSelectedDate(null);
      setDateError('');
      setValidationMessage(null);
      setAutoValidated(false);
    }
  };

  const handleClinicSelect = (clinic) => {
    setSelectedClinic(clinic);
    setSelectedShift(null);
    setSelectedDaysOfWeek([]);
    setSelectedShiftDayIds([]);
    setSelectedAvailabilityHour(null);
    setSelectedDate(null);
    setDateError('');
    setValidationMessage(null);
    setAutoValidated(false);
  };

  const handleShiftSelect = (shift) => {
    if (selectedShift?.shiftId === shift.shiftId) {
      setSelectedShift(null);
      setSelectedDaysOfWeek([]);
      setSelectedShiftDayIds([]);
      setSelectedAvailabilityHour(null);
      setSelectedDate(null);
      setValidationMessage(null);
      setAutoValidated(false);
    } else {
      setSelectedShift(shift);
      setSelectedDaysOfWeek([]);
      setSelectedShiftDayIds([]);
      setSelectedAvailabilityHour(null);
      setSelectedDate(null);
      setValidationMessage(null);
      setAutoValidated(false);
    }
  };

  const handleDayToggle = (dayValue, dayId) => {
    setSelectedDaysOfWeek(prev => {
      const newDays = prev.includes(dayValue)
        ? prev.filter(d => d !== dayValue)
        : [...prev, dayValue].sort((a, b) => a - b);
      return newDays;
    });
    
    setSelectedShiftDayIds(prev => {
      if (prev.includes(dayId)) {
        return prev.filter(id => id !== dayId);
      } else {
        return [...prev, dayId];
      }
    });
    
    setSelectedAvailabilityHour(null);
    setSelectedDate(null);
    setValidationMessage(null);
    setAutoValidated(false);
  };

  const handleAvailabilityHourSelect = (hour) => {
    setSelectedAvailabilityHour(hour);
    setSelectedDate(null);
    setValidationMessage(null);
    setAutoValidated(false);
  };

  const isWorkingDay = (date) => {
    const dayOfWeek = date.day();
    return selectedDaysOfWeek.includes(dayOfWeek);
  };

  const disabledDate = (current) => {
    if (current && current < dayjs().startOf('day')) {
      return true;
    }
    return !isWorkingDay(current);
  };

  const handleDateSelect = (date) => {
    if (disabledDate(date)) {
      setDateError('This date is not available for the selected days');
      return;
    }

    setSelectedDate(date);
    setDateError('');
    setValidationMessage(null);
    setAutoValidated(false);
  };

  // Auto-validate when all required data is complete
  useEffect(() => {
    const shouldAutoValidate = 
      selectedSpecialist &&
      selectedAvailabilityHour &&
      selectedDate &&
      selectedShiftDayIds.length > 0 &&
      numberOfSessions >= 1 &&
      !autoValidated;

    if (shouldAutoValidate) {
      validateSessions();
      setAutoValidated(true);
    }
  }, [selectedSpecialist, selectedAvailabilityHour, selectedDate, selectedShiftDayIds, numberOfSessions, autoValidated]);

  // Helper function to parse time string (HH:mm:ss) to hours
  const parseTimeToHours = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours + minutes / 60;
  };

  // Helper function to format hours back to time string
  const formatHoursToTime = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:00`;
  };

  // Split hourly availability into individual hour slots
  const splitHourlyAvailability = (hour) => {
    const startHours = parseTimeToHours(hour.startTime);
    const endHours = parseTimeToHours(hour.endTime);
    const slots = [];

    for (let i = startHours; i < endHours; i++) {
      slots.push({
        ...hour,
        startTime: formatHoursToTime(i),
        endTime: formatHoursToTime(i + 1),
        originalHourId: hour.hourId,
        slotIndex: i - startHours
      });
    }

    return slots;
  };

  // Get time slots from selected shift
  const getTimeSlots = () => {
    if (!selectedShift || !selectedShift.hours || selectedShift.hours.length === 0) {
      return [];
    }

    const allSlots = [];
    selectedShift.hours.forEach(hour => {
      const slots = splitHourlyAvailability(hour);
      allSlots.push(...slots);
    });

    // Sort by start time
    return allSlots.sort((a, b) => {
      const aStart = parseTimeToHours(a.startTime);
      const bStart = parseTimeToHours(b.startTime);
      return aStart - bStart;
    });
  };

  const availabilityHours = getTimeSlots();

  // Validate sessions before assignment
  const validateSessions = async () => {
    if (!selectedSpecialist || !selectedAvailabilityHour || !selectedDate || selectedShiftDayIds.length === 0) {
      message.warning('Please complete all selections before validating');
      return false;
    }

    setValidating(true);
    setValidationMessage(null);
const date = new Date(selectedDate); // التاريخ اللي اخترتيه
const utcDate = new Date(Date.UTC(
  date.getFullYear(),
  date.getMonth(),
  date.getDate(),
  0, 0, 0
));
    try {
      const requestBody = {
        specialistId: selectedSpecialist.specialistId,
        shiftHourId: selectedAvailabilityHour.originalHourId || selectedAvailabilityHour.hourId,
  startDate: utcDate.toISOString(),
        numberOfSessions: numberOfSessions || 0,
        selectedHour: selectedAvailabilityHour.startTime,
        shiftDayIds: selectedShiftDayIds
      };

      const response = await drEasyApi.post('/api/PatientSessions/ValidateSessions', requestBody);
      
      const validationMsg = response.data?.[0] || 'Validation successful';
      setValidationMessage({
        type: 'success',
        text: validationMsg
      });
      
      return true;
    } catch (error) {
      console.error('Error validating sessions:', error);
      const errorMsg = error.response?.data?.[0] || 'Some sessions are not available. Please adjust your selection.';
      setValidationMessage({
        type: 'error',
        text: errorMsg
      });
      return false;
    } finally {
      setValidating(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedSpecialist) {
      message.warning('Please select a specialist');
      return;
    }

    if (!selectedAvailabilityHour) {
      message.warning('Please select an availability hour');
      return;
    }

    if (!selectedDate) {
      message.warning('Please select a start date');
      return;
    }

    if (selectedShiftDayIds.length === 0) {
      message.warning('Please select at least one day of the week');
      return;
    }

    if (!numberOfSessions || numberOfSessions < 1) {
      message.warning('Please enter a valid number of sessions');
      return;
    }

    // Check if validation was successful
    if (!validationMessage || validationMessage.type !== 'success') {
      message.warning('Please wait for validation to complete successfully');
      return;
    }
const date = new Date(selectedDate); 
const utcDate = new Date(Date.UTC(
  date.getFullYear(),
  date.getMonth(),
  date.getDate(),
  0, 0, 0
));
    setAssigningId(selectedSpecialist.specialistId);
    try {
      const requestBody = {
        userId: booking?.userId || booking?.patientId,
        specialistId: selectedSpecialist.specialistId,
        doctorId: booking?.doctorId||selectedSpecialist.specialistId,
        shiftHourId: selectedAvailabilityHour.originalHourId || selectedAvailabilityHour.hourId,
  startDate: utcDate.toISOString(),
        numberOfSessions: numberOfSessions,
        selectedHour: selectedAvailabilityHour.startTime,
        shiftDayIds: selectedShiftDayIds
      };

      await drEasyApi.post('/api/PatientSessions/AssignToSpecialist', requestBody);

      message.success(
        `Successfully assigned ${booking?.patientName} to ${selectedSpecialist.name} for ${numberOfSessions} session(s)`
      );
      onAssign(selectedSpecialist);
      handleCompleteClick(booking)
      handleCancel();
    } catch (error) {
      console.error('Error assigning specialist:', error);
      message.error(
        error.response?.data?.[0] ||
          'Failed to assign specialist. Please try again.'
      );
    } finally {
      setAssigningId(null);
    }
  };

  const handleCancel = () => {
    resetSelection();
    setSearchTerm('');
    setFilterWorkingDay(null);
    setPageNumber(1);
    onCancel();
  };

  const availableClinics = selectedSpecialist?.clinics || [];
  
  // Get shifts from selected clinic
  const availableShifts = selectedClinic?.shifts || [];

  // Generate calendar days
  const startOfMonth = currentMonth.startOf('month');
  const endOfMonth = currentMonth.endOf('month');
  const startDay = startOfMonth.day();
  const daysInMonth = endOfMonth.date();
  const days = [];

  for (let i = 0; i < startDay; i++) {
    days.push(null);
  }

  for (let i = 1; i <= daysInMonth; i++) {
    const date = startOfMonth.set('date', i);
    days.push(date);
  }

  const remainingSlots = 42 - days.length;
  for (let i = 0; i < remainingSlots; i++) {
    days.push(null);
  }

  const formatTime12Hour = (time24) => {
    if (!time24) return '';
    
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <Modal
      title={
        <div className="flex items-center">
          <div
            className={`${
              isMobile ? 'w-6 h-6' : 'w-8 h-8'
            } bg-primary rounded-full flex items-center justify-center ${
              isMobile ? 'mr-2' : 'mr-3'
            }`}
          >
            <FaUserMd className={`text-white ${isMobile ? 'text-xs' : 'text-sm'}`} />
          </div>
          <span className={isMobile ? 'text-sm' : ''}>
            Assign Specialist to {booking?.patientName}
          </span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      width={isMobile ? '95vw' : 900}
      centered={isMobile}
      className="specialist-assignment-modal"
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="validate"
          onClick={validateSessions}
          loading={validating}
          disabled={!selectedSpecialist || !selectedAvailabilityHour || !selectedDate || selectedShiftDayIds.length === 0}
          style={{
            backgroundColor: selectedSpecialist && selectedAvailabilityHour && selectedDate && selectedShiftDayIds.length > 0 ? '#1890ff' : '#ccc',
            borderColor: selectedSpecialist && selectedAvailabilityHour && selectedDate && selectedShiftDayIds.length > 0 ? '#1890ff' : '#ccc',
            color: 'white'
          }}
        >
          Validate Sessions
        </Button>,
        <Button
          key="assign"
          type="primary"
          onClick={handleAssign}
          loading={assigningId !== null}
          disabled={!selectedSpecialist || !selectedAvailabilityHour || !selectedDate || selectedShiftDayIds.length === 0 || !numberOfSessions}
          style={{
            backgroundColor: selectedSpecialist && selectedAvailabilityHour && selectedDate && selectedShiftDayIds.length > 0 ? '#298d9f' : '#ccc',
            borderColor: selectedSpecialist && selectedAvailabilityHour && selectedDate && selectedShiftDayIds.length > 0 ? '#298d9f' : '#ccc',
          }}
        >
          Assign Specialist
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <div className={`space-y-4 ${isMobile ? 'mb-4' : 'mb-6'}`}>
          <Input
            placeholder="Search by specialist name"
            prefix={<SearchOutlined className="text-gray-400" />}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            size={isMobile ? 'middle' : 'large'}
            className="rounded-xl border-gray-200 hover:border-blue-400 focus:border-blue-500 transition-colors duration-300"
          />

          <Select
            placeholder="📅 Filter by working day"
            allowClear
            value={filterWorkingDay}
            onChange={handleWorkingDayChange}
            size={isMobile ? 'middle' : 'large'}
            className="w-full rounded-xl"
          >
            {workingDays.map((day) => (
              <Select.Option key={day.value} value={day.value}>
                {day.label}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div className={isMobile ? 'text-sm font-semibold mb-3' : 'text-base font-semibold mb-4'}>
          📋 Select Specialist ({totalCount} found)
        </div>
        {specialists.length > 0 ? (
          <div
            className={`${
              isMobile ? 'max-h-56' : 'max-h-64'
            } overflow-y-auto space-y-3 mb-4`}
          >
            {specialists.map((specialist) => (
              <Card
                key={specialist.specialistId}
                className={`cursor-pointer rounded-lg transition-all duration-300 border-2 ${
                  selectedSpecialist?.specialistId === specialist.specialistId
                    ? 'border-primary bg-blue-50 shadow-lg'
                    : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                }`}
                onClick={() => handleSpecialistSelect(specialist)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div
                      className={`${
                        isMobile ? 'w-10 h-10' : 'w-12 h-12'
                      } bg-gradient-to-r from-primary to-primary_hover rounded-full flex items-center justify-center text-white font-bold flex-shrink-0`}
                    >
                      {specialist.photoId ? (
                        <img
                          src={getImg(specialist.photoId)}
                          alt={specialist.name}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <UserOutlined />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <h4
                        className={`font-semibold text-gray-800 ${
                          isMobile ? 'text-sm' : 'text-base'
                        }`}
                      >
                        {specialist.name}
                      </h4>
                      {specialist.specialization && (
                        <p
                          className={`text-gray-600 ${
                            isMobile ? 'text-xs' : 'text-sm'
                          }`}
                        >
                          {specialist.specialization}
                        </p>
                      )}
                      {specialist.clinics && specialist.clinics.length > 0 && (
                        <p
                          className={`text-gray-500 ${
                            isMobile ? 'text-xs' : 'text-sm'
                          }`}
                        >
                          📍 {specialist.clinics.length} clinic(s) available
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex-shrink-0 ml-2">
                    {selectedSpecialist?.specialistId === specialist.specialistId && (
                      <div className="flex items-center justify-center w-6 h-6 bg-primary rounded-full">
                        <CheckOutlined className="text-white text-xs" />
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Empty
            description={loading ? 'Loading specialists...' : 'No specialists found'}
            className="py-8 mb-4"
          />
        )}

        {selectedSpecialist && availableClinics.length > 0 && (
          <>
            <Divider className="my-3 sm:my-4" />
            <div className={isMobile ? 'text-sm font-semibold mb-3' : 'text-base font-semibold mb-4'}>
              🏥 Select Clinic
            </div>
            <div className="space-y-2 mb-4">
              {availableClinics.map((clinic) => (
                <Card
                  key={clinic.clinicId}
                  className={`cursor-pointer rounded-lg transition-all duration-300 border-2 ${
                    selectedClinic?.clinicId === clinic.clinicId
                      ? 'border-primary bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  onClick={() => handleClinicSelect(clinic)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className={`font-semibold text-gray-800 ${isMobile ? 'text-sm' : ''}`}>
                        <EnvironmentOutlined className="mr-2" />
                        {clinic.clinicName}
                      </div>
                      {clinic.detailedAddress && (
                        <div className={`text-gray-600 mt-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                          {clinic.detailedAddress}
                        </div>
                      )}
                      {clinic.contactPhone && (
                        <div className={`text-gray-500 mt-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                          📞 {clinic.contactPhone}
                        </div>
                      )}
                    </div>
                    {selectedClinic?.clinicId === clinic.clinicId && (
                      <CheckOutlined className="text-primary" />
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}

        {selectedClinic && availableShifts.length > 0 && (
          <>
            <Divider className="my-3 sm:my-4" />
            <div className={isMobile ? 'text-sm font-semibold mb-3' : 'text-base font-semibold mb-4'}>
              🕐 Select Shift
            </div>
            <div className="space-y-3 mb-4">
              {availableShifts.map((shift, index) => {
                const workingDaysInShift = shift.days?.filter(d => d.isWorking) || [];
                const hasHours = shift.hours && shift.hours.length > 0;
                
                if (workingDaysInShift.length === 0) return null;

                return (
                  <Card
                    key={shift.shiftId}
                    className={`cursor-pointer rounded-lg transition-all duration-300 border-2 ${
                      selectedShift?.shiftId === shift.shiftId
                        ? 'border-primary bg-blue-50 shadow-lg'
                        : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                    }`}
                    onClick={() => handleShiftSelect(shift)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className={`font-semibold text-gray-800 mb-2 ${isMobile ? 'text-sm' : 'text-base'}`}>
                          Shift {index + 1}
                        </div>
                        <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                          📅 Days: {workingDaysInShift.map(d => 
                            workingDays.find(wd => wd.value === d.dayOfWeek)?.label
                          ).join(', ')}
                        </div>
                    {hasHours && (
  <div className={`mt-2 flex flex-wrap gap-2 ${isMobile ? 'text-xs' : 'text-sm'}`}>
    <span className="text-gray-600 w-full mb-1">⏰ Available Slots:</span>
    {shift.hours.map((hour, hIdx) => (
      <span 
        key={hIdx} 
        className="px-2 py-1 bg-gray-100 border border-gray-200 rounded-md text-gray-700 flex items-center"
      >
        <ClockCircleOutlined className="mr-1 text-xs" />
        {formatTime12Hour(hour.startTime)} - {formatTime12Hour(hour.endTime)}
      </span>
    ))}
  </div>
)}
                        {!hasHours && (
                          <div className={`text-orange-600 mt-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                            ⚠️ No time slots configured
                          </div>
                        )}
                      </div>
                      {selectedShift?.shiftId === shift.shiftId && (
                        <CheckOutlined className="text-primary text-lg" />
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          </>
        )}

        {selectedShift && (
          <>
            <Divider className="my-3 sm:my-4" />
            <div className={isMobile ? 'text-sm font-semibold mb-3' : 'text-base font-semibold mb-4'}>
              📅 Select Days (Multiple Selection)
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
              {selectedShift.days?.filter(d => d.isWorking).map((day) => {
                const dayLabel = workingDays.find(d => d.value === day.dayOfWeek)?.label || `Day ${day.dayOfWeek}`;
                const isSelected = selectedDaysOfWeek.includes(day.dayOfWeek);
                
                return (
                  <Card
                    key={day.dayId}
                    className={`cursor-pointer rounded-lg transition-all duration-300 border-2 ${
                      isSelected
                        ? 'border-primary bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    onClick={() => handleDayToggle(day.dayOfWeek, day.dayId)}
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox checked={isSelected} />
                      <div className={`font-semibold text-gray-800 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                        {dayLabel}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
         
          </>
        )}

        {selectedDaysOfWeek.length > 0 && selectedShift && (
          <>
            <Divider className="my-3 sm:my-4" />
            <div className={isMobile ? 'text-sm font-semibold mb-3' : 'text-base font-semibold mb-4'}>
              🕐 Select Time Slot
            </div>
            {availabilityHours.length > 0 ? (
              <div className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-3'} gap-2 mb-4`}>
                {availabilityHours.map((hour, index) => {
                  const uniqueKey = hour.originalHourId 
                    ? `${hour.originalHourId}-${hour.slotIndex}` 
                    : `${hour.hourId}-${index}`;
                  
                  return (
                    <Card
                      key={uniqueKey}
                      className={`cursor-pointer rounded-lg transition-all duration-300 border-2 ${
                        selectedAvailabilityHour && 
                        selectedAvailabilityHour.startTime === hour.startTime &&
                        selectedAvailabilityHour.endTime === hour.endTime
                          ? 'border-primary bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                      onClick={() => handleAvailabilityHourSelect(hour)}
                    >
                      <div className="text-center">
                        <div className={`font-semibold text-gray-800 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                          <ClockCircleOutlined className="mr-1" />
                          {formatTime12Hour(hour.startTime.substring(0, 5))} - {formatTime12Hour(hour.endTime.substring(0, 5))}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Alert
                message="No time slots available for this shift"
                description="This shift doesn't have any configured time slots"
                type="warning"
                showIcon
                className="mb-4"
              />
            )}
          </>
        )}

        {selectedAvailabilityHour && selectedDaysOfWeek.length > 0 && (
          <>
            <Divider className="my-3 sm:my-4" />
            <div className={isMobile ? 'text-sm font-semibold mb-3' : 'text-base font-semibold mb-4'}>
              <FaRegCalendarDays className="inline mr-2 text-primary" />
              Select Start Date
            </div>

            {dateError && <Alert message={dateError} type="error" showIcon className="mb-4" />}

            <div className="border border-textPrimary rounded-lg shadow-sm overflow-hidden max-w-xl mx-auto">
              <div className="flex justify-between items-center p-3 bg-primary border-b border-textPrimary">
                <Button
                  type="text"
                  icon={<FaChevronLeft />}
                  className="text-white"
                  onClick={() => setCurrentMonth(currentMonth.subtract(1, 'month'))}
                />
                <div className="text-center">
                  <div className="text-lg font-semibold text-white">
                    {currentMonth.format('MMMM YYYY')}
                  </div>
                </div>
                <Button
                  type="text"
                  icon={<FaChevronRight />}
                  className="text-white"
                  onClick={() => setCurrentMonth(currentMonth.add(1, 'month'))}
                />
              </div>

              <div className="grid grid-cols-7 bg-gray-50">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day, index) => (
                  <div
                    key={index}
                    className="text-sm font-medium text-placeHolder text-center py-2 border border-textPrimary bg-cardColor"
                  >
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 bg-white">
                {days.map((day, index) => {
                  if (!day) {
                    return (
                      <div
                        key={index}
                        className="h-12 border border-textPrimary bg-gray-50"
                      />
                    );
                  }

                  const isSelected = selectedDate && dayjs(selectedDate).isSame(day, 'day');
                  const isOtherMonth = !day.isSame(currentMonth, 'month');
                  const isDisabled = disabledDate(day);

                  return (
                    <div
                      key={index}
                      className={`h-12 flex items-center justify-center border cursor-pointer border-textPrimary
                        ${isSelected ? ' text-white' :
                          isDisabled ? 'bg-gray-50 text-gray-300 cursor-not-allowed' :
                            isOtherMonth ? 'bg-gray-50 text-gray-400' :
                              'hover:bg-page '}
                      `}
                      onClick={() => !isDisabled && !isOtherMonth && handleDateSelect(day)}
                    >
                      <div className={`flex items-center justify-center rounded-full w-8 h-8
                        ${isSelected ? 'bg-secondary text-white' : ''}`}>
                        {day.date()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {selectedAvailabilityHour && selectedDate && (
          <>
            <Divider className="my-3 sm:my-4" />
            <div className={isMobile ? 'text-sm font-semibold mb-3' : 'text-base font-semibold mb-4'}>
               Number of Sessions
            </div>
            <InputNumber
              min={1}
              max={100}
              value={numberOfSessions}
              onChange={setNumberOfSessions}
              className="w-full"
              size={isMobile ? 'middle' : 'large'}
              placeholder="Enter number of sessions"
            />
          </>
        )}

        {validationMessage && (
          <Alert
            message={validationMessage.text}
            type={validationMessage.type}
            showIcon
            className="mt-4"
          />
        )}

        {selectedSpecialist && selectedAvailabilityHour && selectedDate && selectedDaysOfWeek.length > 0 && (
          <div
            className={`mt-4 p-3 bg-green-50 border border-green-200 rounded-lg space-y-2 ${
              isMobile ? 'text-xs' : 'text-sm'
            }`}
          >
            <div className="font-semibold text-green-800">✅ Assignment Summary</div>
            <div className="text-green-700">
              • <span className="font-medium">Specialist:</span> {selectedSpecialist.name}
            </div>
            {selectedClinic && (
              <div className="text-green-700">
                • <span className="font-medium">Clinic:</span> {selectedClinic.clinicName}
              </div>
            )}
            <div className="text-green-700">
              • <span className="font-medium">Days:</span>{' '}
              {selectedDaysOfWeek.map(d => workingDays.find(wd => wd.value === d)?.label).join(', ')}
            </div>
            <div className="text-green-700">
              • <span className="font-medium">Time:</span> {formatTime12Hour(selectedAvailabilityHour.startTime.substring(0, 5))} -{' '}
              {formatTime12Hour(selectedAvailabilityHour.endTime.substring(0, 5))}
            </div>
            <div className="text-green-700">
              • <span className="font-medium">Start Date:</span>{' '}
              {dayjs(selectedDate).format('MMM D, YYYY')}
            </div>
            <div className="text-green-700">
              • <span className="font-medium">Sessions:</span> {numberOfSessions}
            </div>
          </div>
        )}

        {selectedSpecialist && availableClinics.length === 0 && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className={`text-yellow-800 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              ⚠ This specialist has no clinics configured. Please add clinic information first.
            </div>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default SpecialistAssignmentModal;